import angularModule from 'Settings/angularModule/scripts/settings_module';
import template from 'Settings/angularModule/views/tuition_and_registration/cohort_registration_tuition_ledger.html';
import cacheAngularTemplate from 'cacheAngularTemplate';
import { paymentSituationWithFormattedValues } from 'PaymentSituation';
import { isFullScholarship, isPartialScholarship } from 'Scholarship';
import { formatCurrencyForDisplay } from 'Currency';
import { statsigClientProvider } from 'StatsigClientProvider';

const templateUrl = cacheAngularTemplate(angularModule, template);

angularModule.directive('cohortRegistrationTuitionLedger', [
    '$injector',

    function factory($injector) {
        const TranslationHelper = $injector.get('TranslationHelper');
        const Cohort = $injector.get('Cohort');
        const $rootScope = $injector.get('$rootScope');

        return {
            restrict: 'E',
            templateUrl,
            scope: {
                admissionOffer: '<',
                cohortSectionOffer: '<',
            },
            link(scope) {
                Object.defineProperty(scope, 'currentUser', {
                    get() {
                        return $rootScope.currentUser;
                    },
                    configurable: true,
                });

                const translationHelper = new TranslationHelper('settings.tuition_and_registration');

                scope.hasPartialScholarship = isPartialScholarship(
                    scope.cohortSectionOffer.cumulativeOfferedScholarship,
                );
                scope.hasFullScholarship = isFullScholarship(scope.cohortSectionOffer.cumulativeOfferedScholarship);
                scope.hasNoScholarship = !scope.hasPartialScholarship && !scope.hasFullScholarship;

                const basePaymentSituation = paymentSituationWithFormattedValues(
                    scope.cohortSectionOffer.basePaymentSituation,
                );

                // As of right now, only ExecEd programs hide their program title in the
                // base tuition row of the tuition ledger. If this logic needs to expand to other program types,
                // it may be a better idea to create a schedulable.js flag to control this behavior.
                const hideProgramTitleInBaseTuitionRow = Cohort.isExecEd(scope.admissionOffer.programType);
                const titleLocaleKey = (() => {
                    let key = 'base_tuition';

                    if (scope.hasNoScholarship) {
                        key += '_no_scholarship';
                    } else if (hideProgramTitleInBaseTuitionRow) {
                        key += '_no_program_title';
                    }

                    return key;
                })();

                // NOTE: getTuitionLedgerRowsWithMonthlyBreakdown and getTuitionLedgerRowsWithMonthlyBreakdown are very WET here, and that's intentional.
                // The tuition ledger was originally implemented in a way that displayed line items broken down into their monthly payment equivalent.
                // We are changing things such that they'll now be displayed as a factor of total tuition, which is more intuitive to users.
                // However, we're putting this change behind a feature gate in case we need to disable it and go back to showing the old UI.
                // Keeping the methods WET like this makes the cleanup easier when we come back in to remove the gate in favor of one UI implementation
                // over the other.
                // I don't anticipate us leaving this code around for long after enabling the tuition_ledger_without_monthly gate on prod, so the duplication
                // doesn't really scare me unless we need to maintain both implementations for a long period of time.

                // FIXME: remove this when we remove the tuition_ledger_without_monthly gate if we decided to fully adopt
                // the new UI implementation.
                const getTuitionLedgerRowsWithMonthlyBreakdown = () => {
                    const tuitionLedgerRows = [];

                    const baseTuitionRow = {
                        titleLocale: translationHelper.get(titleLocaleKey, {
                            programTitle: Cohort.fullTitle(scope.admissionOffer.programType),
                        }),
                        costOrDiscountLocale: translationHelper.get('cost_of_ledger_item', {
                            value: basePaymentSituation.paymentInformation.formattedFullTuitionPaymentPerInterval,
                        }),
                        totalAmountLocale: translationHelper.get('total_amount', {
                            numIntervals: basePaymentSituation.tuitionPlan.numIntervals,
                            totalAmount: formatCurrencyForDisplay(basePaymentSituation.paymentInformation.baseTuition),
                        }),
                        className: 'base-tuition-row with-monthly-breakdown',
                    };

                    tuitionLedgerRows.push(baseTuitionRow);

                    if (scope.hasPartialScholarship || scope.hasFullScholarship) {
                        const scholarshipTranslationHelper = new TranslationHelper('scholarship_labels');

                        const scholarshipRows = scope.cohortSectionOffer.offeredScholarships?.map(scholarship => {
                            // See https://trello.com/c/MzuW1e1J
                            const meritLabelOverride =
                                ['strada', 'zenith'].includes(scholarship.label) &&
                                scope.admissionOffer.meritLabelOverride;

                            return {
                                titleLocale:
                                    meritLabelOverride ||
                                    scholarshipTranslationHelper.getWithFallbackKey(
                                        scholarship.label,
                                        // Fallback if scholarshipLabel isn't defined in the JSON for the current user's scholarship
                                        'base_scholarship',
                                        {
                                            // Not all scholarship labels have the year interpolated into them, but it's harmless to always include it.
                                            offeredYear: scope.admissionOffer.offeredAt
                                                ? new Date(scope.admissionOffer.offeredAt * 1000).getFullYear()
                                                : '',
                                        },
                                        {},
                                        undefined,
                                        undefined,
                                        false,
                                    ),
                                costOrDiscountLocale: translationHelper.get('discount_of_ledger_item', {
                                    value: scholarship.amount / basePaymentSituation.tuitionPlan.numIntervals,
                                }),
                                className: 'scholarship-row with-monthly-breakdown',
                            };
                        });

                        const totalTuitionRow = {
                            titleLocale: translationHelper.get('total_tuition'),
                            costOrDiscountLocale: translationHelper.get('cost_of_ledger_item', {
                                value: scope.hasFullScholarship
                                    ? '0'
                                    : basePaymentSituation.paymentInformation.formattedPaymentAmountPerInterval,
                            }),
                            totalAmountLocale: scope.hasFullScholarship
                                ? translationHelper.get('full_scholarship')
                                : translationHelper.get('total_amount', {
                                      numIntervals: basePaymentSituation.tuitionPlan.numIntervals,
                                      totalAmount: formatCurrencyForDisplay(
                                          basePaymentSituation.paymentInformation.netRequiredPayment,
                                      ),
                                  }),
                            className: 'total-tuition-row with-monthly-breakdown',
                        };

                        tuitionLedgerRows.push(...scholarshipRows, totalTuitionRow);
                    }

                    return tuitionLedgerRows;
                };

                const getTuitionLedgerRowsWithoutMonthlyBreakdown = () => {
                    const tuitionLedgerRows = [];

                    const baseTuitionRow = {
                        titleLocale: translationHelper.get(titleLocaleKey, {
                            programTitle: Cohort.fullTitle(scope.admissionOffer.programType),
                        }),
                        costOrDiscountLocale: translationHelper.get('cost_of_ledger_item', {
                            value: formatCurrencyForDisplay(basePaymentSituation.paymentInformation.baseTuition),
                        }),
                        className: 'base-tuition-row',
                    };

                    tuitionLedgerRows.push(baseTuitionRow);

                    if (scope.hasPartialScholarship || scope.hasFullScholarship) {
                        const scholarshipTranslationHelper = new TranslationHelper('scholarship_labels');

                        const scholarshipRows = scope.cohortSectionOffer.offeredScholarships?.map(scholarship => {
                            // See https://trello.com/c/MzuW1e1J
                            const meritLabelOverride =
                                ['strada', 'zenith'].includes(scholarship.label) &&
                                scope.admissionOffer.meritLabelOverride;

                            return {
                                titleLocale:
                                    meritLabelOverride ||
                                    scholarshipTranslationHelper.getWithFallbackKey(
                                        scholarship.label,
                                        // Fallback if scholarshipLabel isn't defined in the JSON for the current user's scholarship
                                        'base_scholarship',
                                        {
                                            // Not all scholarship labels have the year interpolated into them, but it's harmless to always include it.
                                            offeredYear: scope.admissionOffer.offeredAt
                                                ? new Date(scope.admissionOffer.offeredAt * 1000).getFullYear()
                                                : '',
                                        },
                                        {},
                                        undefined,
                                        undefined,
                                        false,
                                    ),
                                costOrDiscountLocale: translationHelper.get('discount_of_ledger_item', {
                                    value: formatCurrencyForDisplay(scholarship.amount),
                                }),
                                className: 'scholarship-row',
                            };
                        });

                        const totalTuitionRow = {
                            titleLocale: translationHelper.get('total_tuition'),
                            costOrDiscountLocale: translationHelper.get('cost_of_ledger_item', {
                                value: scope.hasFullScholarship
                                    ? '0'
                                    : formatCurrencyForDisplay(
                                          basePaymentSituation.paymentInformation.netRequiredPayment,
                                      ),
                            }),
                            className: 'total-tuition-row',
                        };

                        tuitionLedgerRows.push(...scholarshipRows, totalTuitionRow);
                    }

                    return tuitionLedgerRows;
                };

                // I'm intentionally using `checkGate` instead of `waitForGate` here for the following reasons:
                // 1. This blocks render so it's risky having an async call here.
                // 2. It shouldn't be possible to get to the registration page without a loaded statsig client.
                // 3. A minor flash of incorrect UI (showing the monthly breakdown vs. not) is probably not a big deal here.
                const showTuitionLedgerWithoutMonthly = statsigClientProvider.checkGate(
                    'tuition_ledger_without_monthly',
                );

                scope.tuitionLedgerRows = showTuitionLedgerWithoutMonthly
                    ? getTuitionLedgerRowsWithoutMonthlyBreakdown()
                    : getTuitionLedgerRowsWithMonthlyBreakdown();
            },
        };
    },
]);
